import { useState } from "react";

import { Button, Callout, Intent } from "@blueprintjs/core";

import { Box } from "@/components/layout/flexbox";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import {
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  useRemoveProgramComponentMutation,
} from "@/graphql";
import {
  LeadComponent,
  MainComponent,
  ParticipantComponent,
} from "@/types/programs";

import { ReleaseComponentOption } from "./ReleaseComponentOption";

interface DeleteProgramComponentDialogProps {
  programId: string;
  open: boolean;
  onClose: () => void;
  component: MainComponent | LeadComponent | ParticipantComponent;
  onDelete?: () => void;
}

export function DeleteProgramComponentDialog({
  programId,
  open,
  onClose,
  component,
  onDelete = () => {},
}: DeleteProgramComponentDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isReleaseDependentComponents, setIsReleaseDependentComponents] =
    useState(true);

  const [removeProgramComponent, { loading }] =
    useRemoveProgramComponentMutation({
      onCompleted: (data) => {
        const { ok, msg } = data?.removeProgramComponent || {};
        if (ok) {
          onDelete();
          onClose();
        } else {
          setErrorMessage(msg || "Something went wrong. Please try again.");
        }
      },
      onError: (err) => {
        setErrorMessage(err.message);
      },
      refetchQueries: [
        {
          query: ProgramQueryDocument,
          variables: { programId },
        },
        {
          query: ProgramComponentsQueryDocument,
          variables: { programId },
        },
      ],
    });

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title="Delete Component"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setErrorMessage(undefined);
      }}
    >
      <Box p={24}>
        {errorMessage && (
          <Box mb={3}>
            <Callout icon={null} intent={Intent.DANGER}>
              {errorMessage}
            </Callout>
          </Box>
        )}
        <Box>
          <b>
            Are you sure you want to delete{" "}
            {component?.name ? `"${component?.name}"` : "this"} component?
          </b>
          <ul className="list-disc pl-6" style={{ padding: "8px 16px" }}>
            <li>Members will lose access to this survey.</li>
            <li>The content of your question cannot be restored.</li>
            {"dependentComponents" in component &&
            component.dependentComponents?.length ? (
              <>
                <li style={{ marginTop: 8 }}>
                  <ReleaseComponentOption
                    warningTitle="Deleting this component will affect others that depend on it"
                    component={component}
                    defaultValue={isReleaseDependentComponents}
                    onChange={(value) => {
                      setIsReleaseDependentComponents(value);
                    }}
                  />
                </li>
              </>
            ) : (
              <></>
            )}
          </ul>
        </Box>
        <Box mt={3}>
          <Button
            intent="danger"
            large={true}
            disabled={loading}
            onClick={() => {
              removeProgramComponent({
                variables: {
                  programId,
                  programComponentId: component.id,
                  isReleaseDependentComponents: isReleaseDependentComponents,
                },
              });
            }}
            loading={loading}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </StyledDialog>
  );
}
